import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, forkJoin, of, Subject} from 'rxjs';
import {catchError, finalize, takeUntil} from 'rxjs/operators';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {BankDetails as ClientBankDetails, BusinessDetails, BusinessType, FicaDetails, KycStatus, MerchantVerificationResponse, WebBusinessService} from 'web-angular-client';

import {ROUTES} from '../../app.routes';
import {ASecureForm} from '../../sdk/interface/form/ASecureForm';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {AlertService} from '../../sdk/service/alert.service';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {KycDetail} from '../../sdk/interface/model/kyc-detail';
import {BankDetails} from '../../sdk/interface/model/banking-details';
import {AddressDetail} from '../../sdk/interface/model/address-detail';
import {KycSubmissionService} from './kyc-submission.service';
import {KycProfileDetail} from './model/kyc-profile-detail';
import {KycVerificationSubmission} from './model/kyc-verification-submission';

/**
 * The KYC verification wizard for e-commerce merchants: a four-step form capturing the business
 * profile, KYC, banking and address details the trimmed eCommerce signup did not capture, and
 * submitting them for verification. Also serves the read-only "View submission" mode (route data
 * readOnly) used while a submission is under review. Mirrors the SignUpComponent container so a
 * later story can extract shared wizard components.
 */
@Component({
    selector: 'app-ecommerce-kyc-wizard',
    templateUrl: './ecommerce-kyc-wizard.component.html',
    styleUrls: ['./ecommerce-kyc-wizard.component.scss'],
    standalone: false
})
export class EcommerceKycWizardComponent extends ASecureForm implements OnInit, OnDestroy
{
    currentStep = 0;
    /**
     * The stepper items for p-steps. Structurally typed instead of importing PrimeNG's MenuItem —
     * the test toolchain cannot resolve `primeng/api` (exports-mapped subpath) on a fresh compile.
     */
    kycSteps: { label: string }[];
    readOnly = false;
    /** Whether a verification submission is in flight, so the final step disables its submit button and a repeat click is ignored. */
    submitting = false;
    /**
     * Whether a verification submission has already succeeded. Blocks a re-submission even if the
     * follow-up navigation stalls or fails, so the merchant cannot fire a duplicate submission from
     * the form left live behind the success popup.
     */
    private submissionComplete = false;
    screenWidth: number;
    natureOfBusiness: string;
    businessTypeSubject: BehaviorSubject<BusinessType>;

    prefilledProfileDetail: KycProfileDetail;
    prefilledKycDetail: KycDetail;
    prefilledBankDetail: BankDetails;
    prefilledAddressDetail: AddressDetail;

    profileDetail: KycProfileDetail;
    kycDetail: KycDetail;
    bankingDetails: BankDetails;
    addressDetail: AddressDetail;

    private loadedFicaDetails: FicaDetails;
    private loadedBusinessDetails: BusinessDetails;
    /**
     * Whether loading the merchant's stored FICA or business details failed. Submission is blocked
     * while true: the already-captured fields the wizard does not re-collect — businessName /
     * vatNumber / taxNumber (FICA) and customRefEnabled (business details) — are sourced from those
     * loads, and the backend's full overwrite would null them if we submitted without them.
     * customRefEnabled toggles a live merchant feature, so it must never be changed here by accident.
     */
    private preservedDetailsLoadFailed = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private webBusinessService: WebBusinessService,
        private kycSubmissionService: KycSubmissionService,
        private alertService: AlertService,
        private ngxLoader: NgxUiLoaderService)
    {
        super(authenticationService, environmentService);
        this.businessTypeSubject = new BehaviorSubject<BusinessType>(null);
    }

    /**
     * Resolves the read-only mode from the route, loads the merchant's stored details to pre-fill
     * the steps, and redirects merchants the wizard does not apply to: a verified KYC goes to the
     * e-commerce dashboard, a pending one is only viewable read-only.
     */
    ngOnInit(): void
    {
        this.screenWidth = window.innerWidth;
        this.readOnly = this.route.snapshot.data['readOnly'] ?? false;
        this.updateKycSteps();

        this.ngxLoader.start();
        forkJoin([
            this.webBusinessService.getFicaDetails().pipe(
                catchError((error) =>
                {
                    this.debugOutput('Failed to load FICA details for the KYC wizard', error);
                    this.preservedDetailsLoadFailed = true;
                    return of({} as FicaDetails);
                })),
            this.webBusinessService.getBankingDetails().pipe(
                catchError((error) =>
                {
                    this.debugOutput('Failed to load banking details for the KYC wizard', error);
                    return of({} as ClientBankDetails);
                })),
            this.webBusinessService.getBusinessDetails().pipe(
                catchError((error) =>
                {
                    this.debugOutput('Failed to load business details for the KYC wizard', error);
                    this.preservedDetailsLoadFailed = true;
                    return of({} as BusinessDetails);
                }))
        ]).pipe(takeUntil(this.destroy$), finalize(() => this.ngxLoader.stop()))
            .subscribe(([ficaDetails, bankDetails, businessDetails]:
                            [FicaDetails, ClientBankDetails, BusinessDetails]) =>
            {
                if (this.redirectWhenWizardNotApplicable(ficaDetails))
                {
                    return;
                }
                // Fail fast on the editable wizard: if the preserved details could not be loaded the submission would
                // null them, so surface it now rather than after the merchant fills the whole form. The read-only view
                // renders from the session submission and never submits, so it is unaffected.
                if (!this.readOnly && this.preservedDetailsLoadFailed)
                {
                    this.alertService.error(
                        'We could not load your business details, so verification cannot continue right now. ' +
                        'Please refresh the page and try again.');
                    return;
                }
                this.loadedFicaDetails = ficaDetails;
                this.loadedBusinessDetails = businessDetails;
                this.mapPrefilledDetails(ficaDetails, bankDetails, businessDetails);
                this.businessTypeSubject.next(this.prefilledProfileDetail.businessType);
            });
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
        this.businessTypeSubject.unsubscribe();
    }

    @HostListener('window:resize')
    onResize(): void
    {
        this.screenWidth = window.innerWidth;
        this.updateKycSteps();
    }

    isMobile(): boolean
    {
        return this.screenWidth < 756;
    }

    /**
     * Rebuilds the stepper labels; on mobile only the active step keeps its label, mirroring the
     * signup wizard's stepper behaviour.
     */
    updateKycSteps(): void
    {
        this.kycSteps = [
            {label: (this.currentStep !== 0 && this.isMobile()) ? '' : 'User and Business Profile'},
            {label: (this.currentStep !== 1 && this.isMobile()) ? '' : 'Business Details (Required for KYC)'},
            {label: (this.currentStep !== 2 && this.isMobile()) ? '' : 'Banking Details'},
            {label: (this.currentStep !== 3 && this.isMobile()) ? '' : 'Address Details'}
        ];
    }

    /**
     * Moves the wizard to the next step.
     */
    processNext(): void
    {
        const lastStep = this.kycSteps.length - 1;
        this.currentStep = Math.min(this.currentStep + 1, lastStep);
        this.updateKycSteps();
    }

    /**
     * Moves the wizard to the previous step.
     */
    processPrevious(): void
    {
        this.currentStep = Math.max(this.currentStep - 1, 0);
        this.updateKycSteps();
    }

    /**
     * Navigates back to the e-commerce onboarding landing page.
     */
    backToOnboarding(): void
    {
        this.router.navigate([ROUTES.SECURE.ECOMMERCE.LANDING.path]).then();
    }

    /**
     * Stores step 1's profile details and republishes the chosen business type so step 2 can
     * adjust its registration-number validation.
     *
     * @param profileDetail the profile details captured by step 1
     */
    onProfileDetailSubmission(profileDetail: KycProfileDetail): void
    {
        this.profileDetail = profileDetail;
        this.businessTypeSubject.next(profileDetail.businessType);
    }

    /**
     * Stores step 2's KYC details.
     *
     * @param kycDetail the KYC details captured by step 2
     */
    onKycDetailSubmission(kycDetail: KycDetail): void
    {
        this.kycDetail = kycDetail;
    }

    /**
     * Stores step 3's banking details.
     *
     * @param bankingDetails the banking details captured by step 3
     */
    onBankingDetailsSubmission(bankingDetails: BankDetails): void
    {
        this.bankingDetails = bankingDetails;
    }

    /**
     * The final step's submission: aggregates the four steps' data and submits it for verification,
     * showing the self-verification popup (with the identity-verification link when one was issued)
     * and navigating the merchant off the wizard on success (see navigateAfterSubmission). The
     * already-loaded FICA and business details are passed through so the backend does not overwrite
     * the fields the wizard does not capture.
     *
     * @param addressDetail the address details captured by step 4
     */
    onAddressDetailSubmission(addressDetail: AddressDetail): void
    {
        // Ignore repeat clicks while a submission is in flight or has already succeeded, so neither a
        // double-click nor a click on the form left live behind the success popup (after a stalled
        // navigation) can fire a duplicate verification submission.
        if (this.submitting || this.submissionComplete)
        {
            return;
        }
        this.addressDetail = addressDetail;

        if (this.preservedDetailsLoadFailed)
        {
            this.alertService.error(
                'We could not load your business details, so your verification cannot be submitted safely. ' +
                'Please refresh the page and try again.');
            return;
        }

        const submission: KycVerificationSubmission = {
            profileDetail: this.profileDetail,
            kycDetail: this.kycDetail,
            bankingDetails: this.bankingDetails,
            addressDetail: this.addressDetail
        };

        this.submitting = true;
        this.ngxLoader.start();
        this.kycSubmissionService.submitKycVerification(submission, this.loadedFicaDetails, this.loadedBusinessDetails)
            .pipe(takeUntil(this.destroy$), finalize(() =>
            {
                this.ngxLoader.stop();
                this.submitting = false;
            }))
            .subscribe({
                next: (response: MerchantVerificationResponse) =>
                {
                    this.submissionComplete = true;
                    this.showVerificationPopup(response?.kycVerificationUrl?.trim() || null);
                    this.navigateAfterSubmission();
                },
                error: (error) =>
                {
                    this.alertService.error(error);
                }
            });
    }

    /**
     * Shows the identity-verification popup after a successful submission, mirroring the signup
     * success popup: a "Verify your identity" link when the backend issued an enrollment URL, or an
     * email-fallback message when it did not. The popup is kept open across the single navigation
     * away from the wizard.
     *
     * @param verificationUrl the identity-verification link issued by the submission, or null when none was returned
     */
    private showVerificationPopup(verificationUrl: string | null): void
    {
        this.alertService.success(
            this.buildVerificationMessage(!!verificationUrl),
            true,
            undefined,
            verificationUrl
                ? {type: 'link', url: verificationUrl, label: 'Verify Your Identity', cssClass: 'selfie-verify-btn', openInNewTab: true}
                : undefined);
    }

    /**
     * Navigates to where the merchant belongs after a successful submission, in a single hop. A guest
     * admin stays on the onboarding landing page (their KYC is now pending); every other merchant goes
     * straight to the e-commerce dashboard — the same destination the landing page would redirect them
     * to for a pending KYC. Going there directly avoids a second navigation, which would otherwise clear
     * the verification popup, since the popup is kept alive for only a single navigation.
     */
    private navigateAfterSubmission(): void
    {
        const destination = this.isGuestAdminUser()
            ? ROUTES.SECURE.ECOMMERCE.LANDING.path
            : ROUTES.SECURE.ACCOUNT.ECOMMERCE_DASHBOARD.path;
        this.router.navigate([destination]).then();
    }

    /**
     * Builds the identity-verification popup message. When an enrollment URL is available the merchant
     * completes verification via the footer link or the emailed link; otherwise the email-only fallback
     * is shown.
     *
     * @param hasVerificationUrl whether an identity-verification link was issued
     */
    private buildVerificationMessage(hasVerificationUrl: boolean): string
    {
        const intro = `Your details have been submitted for verification.
                 <br/>
                 <br/>`;

        if (hasVerificationUrl)
        {
            return `${intro}
                 Great! We'll let you know once our compliance team has reviewed your submission.
                 <br/>
                 <br/>
                 One last step: Please complete your identity verification using the button below.
                 <span class="deadline-warning">Hurry - it's only valid for 24 hours!</span>`;
        }

        return `${intro}
                 Should your application be successful, you'll receive an email with your identity verification
                 link. <span class="deadline-warning">It's only valid for 24 hours!</span> If you don't hear from
                 us, please contact support for assistance.`;
    }

    /**
     * Redirects merchants the wizard does not apply to: a verified KYC goes straight to the
     * e-commerce dashboard, a pending submission (backend or this session's) can only be opened
     * in the read-only view, and the read-only view without a pending submission falls back to
     * the editable wizard.
     *
     * @param ficaDetails the merchant's FICA details carrying the KYC status
     * @return whether a redirect was performed
     */
    private redirectWhenWizardNotApplicable(ficaDetails: FicaDetails): boolean
    {
        if (ficaDetails?.kycStatus === KycStatus.VERIFIED)
        {
            this.router.navigate([ROUTES.SECURE.ACCOUNT.ECOMMERCE.path]).then();
            return true;
        }

        const kycPending: boolean = ficaDetails?.kycStatus === KycStatus.PENDING
            || this.kycSubmissionService.getSessionSubmission() !== null;
        if (kycPending && !this.readOnly)
        {
            this.router.navigate([ROUTES.SECURE.ECOMMERCE.KYC_VIEW.path]).then();
            return true;
        }
        if (!kycPending && this.readOnly)
        {
            this.router.navigate([ROUTES.SECURE.ECOMMERCE.KYC.path]).then();
            return true;
        }
        return false;
    }

    /**
     * Maps the merchant's stored details onto the four steps' pre-fill models. In read-only mode
     * the session's mock submission overlays the stored values — those are the values that were
     * actually submitted, and the only source of the client base and average transaction amount.
     */
    private mapPrefilledDetails(
        ficaDetails: FicaDetails, bankDetails: ClientBankDetails, businessDetails: BusinessDetails): void
    {
        const sessionSubmission: KycVerificationSubmission =
            this.readOnly ? this.kycSubmissionService.getSessionSubmission() : null;

        this.prefilledProfileDetail = sessionSubmission?.profileDetail ?? {
            businessDisplayName: businessDetails?.displayName ?? null,
            clientBase: null,
            averageTransactionValue: null,
            businessType: ficaDetails?.businessType ?? BusinessType.SOLE_PROPRIETOR
        };

        this.prefilledKycDetail = sessionSubmission?.kycDetail ?? {
            registrationNumber: ficaDetails?.registrationNumber ?? null,
            directorName: ficaDetails?.directorName ?? null,
            directorSurname: ficaDetails?.directorSurname ?? null,
            directorIdNumber: ficaDetails?.directorIdNumber ?? null,
            fspNumber: ficaDetails?.fspNumber ?? null,
            natureOfBusiness: ficaDetails?.natureOfBusiness ?? null,
            overview: ficaDetails?.overview ?? null,
            websiteUrl: ficaDetails?.websiteUrl ?? null,
            token: null
        };

        this.prefilledBankDetail = sessionSubmission?.bankingDetails ?? {
            bankName: bankDetails?.bankName ?? null,
            bankBranch: bankDetails?.bankBranch ?? null,
            bankAccountNumber: bankDetails?.bankAccountNumber ?? null
        };

        this.prefilledAddressDetail = sessionSubmission?.addressDetail ?? {
            address1: businessDetails?.address?.address1 ?? null,
            address2: businessDetails?.address?.address2 ?? null,
            city: businessDetails?.address?.city ?? null,
            province: businessDetails?.address?.province ?? null,
            postalCode: businessDetails?.address?.postalCode ?? null,
            billingEmailAddress: businessDetails?.billingEmail ?? null
        };

        this.natureOfBusiness = sessionSubmission?.kycDetail?.natureOfBusiness
            ?? ficaDetails?.natureOfBusiness ?? null;
    }
}
